import { GenericStatus } from '@hevoio/hevo-ui-toolkit';
import type { Destination, RawDestination, RawDestinationType } from '../../destinations/interfaces/destination';
import { RawEntityDisplayConfig } from '../../shared/interfaces/entity-display-config';
import { HevoEntityMeta, RawHevoEntityMeta } from '../../shared/interfaces/entity-meta';
import { RawSource, RawSourceType, Source } from '../../sources/interfaces/source';
import { AsyncTaskStatus } from '../../shared/interfaces/async-task';
import { EntityAction, EntityRawAction } from '../../entity-actions/interfaces/entity-actions';
import { EntityBanner, RawEntityBanner } from '../../../banner/interfaces/entityBanner';

export enum IntegrationStatus {
  CREATED = 'CREATED',
  CREATED_SOURCE = 'CREATED_SOURCE',
  CREATED_CATALOG = 'CREATED_CATALOG',
  CREATED_DESTINATION = 'CREATED_DESTINATION',
  CONFIGURED_OBJECT = 'CONFIGURED_OBJECT',
  CONFIGURED_PIPELINE = 'CONFIGURED_PIPELINE',
  INITIALIZING = 'INITIALIZING',
  INITIALIZED = 'INITIALIZED',
  INITIALIZE_FAILED = 'INITIALIZE_FAILED',
  PAUSED = 'PAUSED',
  STREAMING = 'STREAMING',
  DISABLED = 'DISABLED',
  DISABLING = 'DISABLING',
  ENABLING = 'ENABLING',
  DISABLE_FAILED = 'DISABLE_FAILED',
  ENABLE_FAILED = 'ENABLE_FAILED',
  SYNC_TASK_FLOW_FAILED = 'SYNC_TASK_FLOW_FAILED',
  DELETE_FAILED = 'DELETE_FAILED',
  RESTART_FAILED = 'RESTART_FAILED',
  RESTARTING = 'RESTARTING',
  DELETING = 'DELETING',
  DELETED = 'DELETED'
}

export enum PipelineLoadMode {
  APPEND = 'APPEND',
  MERGE = 'MERGE',
  OVERWRITE = 'OVERWRITE'
}

export enum PipelineTransactionalFailureAction {
  ALL_OR_NONE = 'ALL_OR_NONE',
  PARTIAL = 'PARTIAL'
}

export enum PipelineSchemaEvolution {
  ALLOW_COLUMN_LEVEL = 'ALLOW_COLUMN_LEVEL',
  ALLOW_ALL = 'ALLOW_ALL',
  BLOCK_ALL = 'BLOCK_ALL'
}


export enum PipelineReplicationTypeMode {
  INCREMENTAL_ONLY = 'INCREMENTAL_ONLY',
  HISTORICAL_AND_INCREMENTAL = 'HISTORICAL_AND_INCREMENTAL'
}

export enum PipelineEditFlushStatus {
  REQUESTED = 'REQUESTED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export enum IntegrationAction {
  DISABLE = 'DISABLE',
  ENABLE = 'ENABLE',
  RUN_NOW = 'RUN_NOW',
  CHANGE_SCHEDULE = 'CHANGE_SCHEDULE',
  RESTART = 'RESTART',
  DELETE = 'DELETE',
  EDIT = 'EDIT'
}

export enum IntegrationBannerAction {
  SKIP = 'SKIP',
  ACKNOWLEDGE = 'ACKNOWLEDGE',
  RESUME_PIPELINE = 'RESUME_PIPELINE'
}

// ui model
export interface Integration extends HevoEntityMeta {
  id: number;
  seqId: number;
  name: string;
  sourceId: number | null;
  destinationId: number | null;
  destinationPrefix: string | null;
  source: Source | null;
  destination: Destination | null;
  config: PipelineConfig;
  metadata: object | null;
  status: GenericStatus<IntegrationStatus>;
  actions: EntityAction<IntegrationAction>[];
  message: {
    errors: string[] | object[] | null;
    warnings: string[] | object[] | null;
    info: string[] | object[] | null;
  };
}

// backend response
export interface RawIntegration extends RawHevoEntityMeta {
  id: number;
  seq_id: number;
  name?: string;
  source_id?: number | null;
  destination_id?: number | null;
  destination_prefix?: string;
  config: PipelineConfig;
  source?: RawSource | null;
  destination?: RawDestination | null;
  integration_actions: EntityRawAction<IntegrationAction>[];
  status: {
    type: IntegrationStatus;
    display_text: string;
    hover_text?: string | null;
  };
  message: {
    errors: string[] | object[] | null;
    warnings: string[] | object[] | null;
    info: string[] | object[] | null;
  };
  metadata: object | null;
}

export interface PipelineConfig {
  sync_type?: string;
  load_mode?: string;
  schema_evolution?: string;
  sync_frequency_in_ms?: number;
  sync_frequency_in_cron?: string;
  sync_frequency_type?: string;
  batch_transactional_failure?: string;
  object_transactional_failure?: string;
  partition_key?: string;
  replication_type?: string;
  standard_integration_id?: number;
  latency_threshold_alert_enabled?: boolean;
  latency_threshold_in_minutes?: number;
  daily_run_time?: string;
  naming_strategy?: string;
  use_sdp_platform?: boolean
}

export interface RawPipelineObjectSelectionReview {
  updated: number;
  activated: number;
  deactivated: number;
}

export interface RawPipelineReviewContent {
  source: RawEntityDisplayConfig<RawSourceType>;
  destination: RawEntityDisplayConfig<RawDestinationType>;
  integration: RawEntityDisplayConfig<RawIntegration>;
  staged_objects: RawPipelineObjectSelectionReview;
}

export interface RawPipelineReviewSelectionResponse {
  fetch_review: RawPipelineObjectSelectionReview;
  status: AsyncTaskStatus;
  status_reason: string;
  banners: RawIntegrationBanner[];
}

export type RawIntegrationBanner = RawEntityBanner<IntegrationBannerAction>;
export type IntegrationBanner = EntityBanner<IntegrationBannerAction>;

export interface RawIntegrationBannerResponse {
  banners: RawIntegrationBanner[];
}

export type PipelineMenuActions = Exclude<IntegrationAction, IntegrationAction.EDIT>;

export interface SyncFrequencyOption {
  label: string;
  value: number;
}

export interface ReplicationTypeOption {
  label: string;
  value: PipelineReplicationTypeMode;
  helper_text: {
    text: string;
  };
}

export interface StandardPipeline {
  seq_id: string | number;
  source_type_logo_url: string;
  source_name: string;
  dest_type_logo_url: string;
  dest_name: string;
  [key: string]: unknown;
}
